/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import PageModule from "@insite/client-framework/Types/PageModule";
import PageProps from "@insite/client-framework/Types/PageProps";
import CookiePrivacyPolicyPopup from "@insite/content-library/Components/CookiePrivacyPolicyPopup";
import Page, { PageProps as MobiusPageProps } from "@insite/mobius/Page";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@stoneage/App/Store/Reducers";
import { color } from "@stoneage/XDL/Utilities";
import * as React from "react";
import { connect, ConnectedProps } from "react-redux";
import { css } from "styled-components";

export interface FooterStyles {
    page?: MobiusPageProps;
    topContent?: InjectableCss;
}

export const footerStyles: FooterStyles = {
    // <XNG changes>
    page: {
        css: css`
            background-color: ${getColor("common.black")};
        `,
    },
    topContent: {
        css: css`
            padding-top: 32px;
            padding-bottom: 32px;
            margin-bottom: 32px;
            color: ${color("common.ui5")};
            text-align: center;
            border-bottom: 1px solid ${color("common.white", 0.2)};

            p:last-child {
                margin-bottom: 0px;
            }
        `,
    },
    // </ XNG changes>
};
const styles = footerStyles;

const Footer = ({
    id,
    isHomePage,
    isCategoryDetailsPage,
    isContentPage,
    isPdpPage,
    easyLinkScriptSourceURL,
}: PageProps & PropsFromRedux) => {
    const refContainer = React.useRef(null);
    const [isOpen, setIsOpen] = React.useState(false);
    React.useEffect(() => {
        const script = document.createElement("script");
        script.type = "text/javascript";
        script.src = easyLinkScriptSourceURL;
        script.async = false;
        script.defer = true;
        try {
            (refContainer.current as any)?.appendChild(script);
            // eslint-disable-next-line no-empty
        } catch (e) {}
        return () => {
            (refContainer.current as any)?.removeChild(script);
        };
    }, [easyLinkScriptSourceURL]);

    return (
        // <XNG changes>
        <Page
            as="footer"
            fullWidth={[true, true, true, true, true]}
            {...styles.page}
            {...({ isHomePage, isCategoryDetailsPage, isContentPage, isPdpPage } as any)}
        >
            <StyledWrapper {...styles.topContent}>
                <Page as="div">
                    <Zone contentId={id} zoneName="topContent" />
                </Page>
            </StyledWrapper>
            <Page as="div">
                <Zone contentId={id} zoneName="middleContent" />
            </Page>
            <CookiePrivacyPolicyPopup />
            <div ref={refContainer} />
        </Page>
        // </ XNG changes>
    );
};

const mapStateToProps = (state: CustomAppState) => {
    const pageType = getCurrentPage(state).type;

    return {
        isHomePage: pageType === "HomePage",
        isCategoryDetailsPage: pageType === "CategoryDetailsPage",
        isContentPage: pageType === "Page",
        isPdpPage: pageType === "ProductDetailsPage",
        easyLinkScriptSourceURL: (getSettingsCollection(state).customSettings as any).easyLinkScriptSourceURL,
    };
};

const connector = connect(mapStateToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const pageModule: PageModule = {
    component: connector(Footer),
    definition: {
        hasEditableTitle: false,
        hasEditableUrlSegment: false,
        pageType: "System",
    },
};

export default pageModule;

/**
 * @deprecated Use string literal "Footer" instead of this constant.
 */
export const FooterContext = "Footer";
